import { useEffect, useRef, useState, type MouseEvent, type PointerEvent } from "react";

export const PAYEE_ALPHABET = [..."ABCDEFGHIJKLMNOPQRSTUVWXYZ", "#"];
export const payeeHeadingId = (letter: string) => `payees-${letter === "#" ? "other" : letter}`;

export function usePayeeAlphabet(letters: string[]) {
  const nav = useRef<HTMLElement>(null);
  const pointer = useRef<number | null>(null);
  const selected = useRef<string | null>(null);
  const frame = useRef<number | null>(null);
  const suppressClick = useRef(false);
  const [active, setActive] = useState<string | null>(null);
  const [bounds, setBounds] = useState<DOMRect | null>(null);
  const available = letters.join("");

  function jump(letter: string, focus = true) {
    const heading = document.getElementById(payeeHeadingId(letter));
    if (focus) heading?.focus({ preventScroll: true });
    heading?.scrollIntoView({ block: "start", behavior: "instant" });
  }
  function selectAt(y: number, rect: DOMRect) {
    const index = Math.max(0, Math.min(PAYEE_ALPHABET.length - 1,
      Math.floor((y - rect.top) / rect.height * PAYEE_ALPHABET.length)));
    const letter = PAYEE_ALPHABET.slice(index).find(l => letters.includes(l)) ?? letters.at(-1);
    if (!letter || selected.current === letter) return;
    selected.current = letter;
    setActive(letter);
    jump(letter, false);
  }
  function start(event: PointerEvent<HTMLElement>) {
    if (!event.isPrimary || event.button !== 0) return;
    event.preventDefault();
    const rect = event.currentTarget.getBoundingClientRect();
    pointer.current = event.pointerId;
    suppressClick.current = true;
    // Keep the hit regions under the finger while jumping moves the page beneath it.
    setBounds(rect);
    event.currentTarget.setPointerCapture(event.pointerId);
    selectAt(event.clientY, rect);
  }
  function move(event: PointerEvent<HTMLElement>) {
    if (pointer.current !== event.pointerId || !bounds) return;
    const y = event.clientY;
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => { frame.current = null; selectAt(y, bounds); });
  }
  function end(event: PointerEvent<HTMLElement>, cancelled = false) {
    if (pointer.current !== event.pointerId) return;
    if (frame.current !== null) { cancelAnimationFrame(frame.current); frame.current = null; }
    if (!cancelled && bounds) selectAt(event.clientY, bounds);
    pointer.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (!cancelled && selected.current) document.getElementById(payeeHeadingId(selected.current))?.focus({ preventScroll: true });
    selected.current = null;
    setActive(null);
    setBounds(null);
  }
  useEffect(() => {
    return () => { if (frame.current !== null) cancelAnimationFrame(frame.current); };
  }, [available]);
  return {
    nav, active, bounds, jump, start, move, end,
    stopClick: (event: MouseEvent<HTMLElement>) => {
      if (suppressClick.current && event.detail !== 0) { event.preventDefault(); event.stopPropagation(); }
      suppressClick.current = false;
    },
  };
}
